import { Button, buttonVariants } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { cn } from "@fieldwork/ui/lib/utils";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import z from "zod";

import { NotConnected, useServer } from "@/components/server-hooks";
import { ServerPage } from "@/components/server-shell";
import { Chip } from "@/components/ui-bits";
import { useAgentAction, useAgentQuery } from "@/lib/act";

export const Route = createFileRoute("/_auth/servers/$id/people")({
  validateSearch: z.object({ f: z.enum(["all", "review", "confirmed"]).optional(), q: z.string().optional() }),
  component: People,
});

type Person = { org: string; name: string; lines: string[]; status: "confirmed" | "review" | "known" };
type List = { people: Person[]; tables: { org: string; table: string }[]; marker: string; pending: number };

const TONE = { confirmed: "ok", review: "warn", known: "neutral" } as const;

function PeopleBody() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const connected = !!useServer(id).data?.hub.connected;
  const q = useAgentQuery<List>(id, "people_list", {}, connected);
  const [text, setText] = useState(search.q ?? "");
  const f = search.f ?? "all";
  const needle = (search.q ?? "").toLowerCase();

  const all = q.data?.people ?? [];
  const counts = { all: all.length, review: all.filter((p) => p.status === "review").length, confirmed: all.filter((p) => p.status === "confirmed").length };
  const groups = useMemo(() => {
    const shown = all.filter(
      (p) => (f === "all" || p.status === f) && (!needle || `${p.name} ${p.org} ${p.lines.join(" ")}`.toLowerCase().includes(needle)),
    );
    const by = new Map<string, Person[]>();
    for (const p of shown) by.set(p.org, [...(by.get(p.org) ?? []), p]);
    return [...by.entries()];
  }, [all, f, needle]);

  if (!connected) return <NotConnected />;
  if (q.error) return <p className="text-sm text-[#F48A80]">{q.error.message}</p>;
  if (!q.data) return <p className="text-sm text-[#A8A59D]">Loading…</p>;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Filter people" className="flex flex-wrap gap-1.5">
          {(
            [
              ["all", "All", "text-[#111111]"],
              ["review", "Needs review", "text-[#F0B65A]"],
              ["confirmed", "Confirmed by you", "text-[#CFCBC1]"],
            ] as const
          ).map(([k, label, color]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={f === k}
              onClick={() => navigate({ search: { ...search, f: k === "all" ? undefined : k } })}
              className={cn(
                "min-h-9 rounded-[4px] px-3 font-mono text-xs",
                f === k ? "bg-[#ECEAE4] text-[#111111]" : `bg-[#262523] ${color === "text-[#111111]" ? "text-[#CFCBC1]" : color}`,
              )}
            >
              {label} {counts[k]}
            </button>
          ))}
        </div>
        <form
          className="flex flex-[0_1_340px]"
          onSubmit={(e) => {
            e.preventDefault();
            navigate({ search: { ...search, q: text.trim() || undefined } });
          }}
        >
          <Input type="search" className="h-10 bg-[#1A1A19]" aria-label="Search people" placeholder="Search name, handle, organisation…" value={text} onChange={(e) => setText(e.target.value)} />
        </form>
      </div>

      {q.data.pending > 0 && (
        <p className="m-0 rounded-md bg-[rgba(232,163,61,0.08)] px-[18px] py-3.5 text-[#CFCBC1] shadow-[inset_0_0_0_1px_rgba(232,163,61,0.35)]">
          <span className="font-semibold text-[#F0B65A]">
            {q.data.pending} edit{q.data.pending > 1 ? "s" : ""} waiting:
          </span>{" "}
          they apply as soon as the current agent run finishes.
        </p>
      )}

      {groups.length === 0 && <p className="text-sm text-[#A8A59D]">Nobody matches.</p>}
      {groups.map(([org, ps], i) => (
        <section key={org} className="flex flex-col gap-2.5">
          <h2 className={cn("m-0 font-mono text-[15px] font-bold", i > 0 && "mt-2")}>
            {org || "Other"} <span className="font-normal text-[#A8A59D]">· {ps.length} {ps.length === 1 ? "person" : "people"}</span>
          </h2>
          {ps.map((p) => (
            <PersonCard key={p.name} id={id} p={p} marker={q.data!.marker} open={!!needle} />
          ))}
        </section>
      ))}

      {f === "all" && !needle && q.data.tables.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer font-mono text-xs text-[#A8A59D]">Tables in your people list ({q.data.tables.length})</summary>
          {q.data.tables.map((t, i) => (
            <div key={i} className="mt-3 rounded-md bg-[#1A1A19] px-5 py-4 shadow-[inset_0_0_0_1px_#2A2927]">
              <p className="m-0 mb-2 font-mono text-xs text-[#A8A59D]">{t.org}</p>
              <pre className="m-0 overflow-x-auto whitespace-pre font-mono text-[12px]">{t.table}</pre>
            </div>
          ))}
        </details>
      )}
    </>
  );
}

function PersonCard({ id, p, marker, open: initiallyOpen }: { id: string; p: Person; marker: string; open: boolean }) {
  const [open, setOpen] = useState(initiallyOpen || p.status === "review");
  const [note, setNote] = useState("");
  const edit = useAgentAction<{ type: "confirm" | "note"; text?: string }, { pending: number }>(
    id,
    "people_edit",
    (v) => ({ ...v, person: p.name }),
    { refresh: ["people_list"], success: (r) => (r.pending ? "Saved; applies when the current run finishes" : "Saved") },
  );
  const summary = p.lines.find((l) => /^(role|relationship|org)/i.test(l)) ?? p.lines[0] ?? "";
  const mk = marker.toLowerCase();
  return (
    <article
      className={cn(
        "flex flex-col rounded-md bg-[#1A1A19] px-[22px]",
        open ? "gap-3.5 py-[18px]" : "gap-3 py-4",
        p.status === "review"
          ? "shadow-[inset_0_0_0_1px_rgba(232,163,61,0.35)]"
          : open
            ? "shadow-[inset_0_0_0_1px_#3A3935]"
            : "shadow-[inset_0_0_0_1px_#2A2927]",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <h3 className={cn("m-0 font-semibold", open ? "text-base" : "text-[15px]")}>{p.name}</h3>
          <Chip value={TONE[p.status]} label={p.status} />
          {!open && <span className="truncate text-[#A8A59D]">{summary.replace(/^[\w /]+:\s*/, "")}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {p.status === "review" && (
            <Button size="sm" disabled={edit.isPending} onClick={() => edit.mutate({ type: "confirm" })}>
              Looks right
            </Button>
          )}
          <Link
            to="/servers/$id/graph"
            params={{ id }}
            search={{ focus: `person:${p.name}` }}
            className="inline-flex min-h-9 items-center px-2 font-mono text-xs text-[#A8A59D] hover:text-[#FF4F1A]"
          >
            show in graph
          </Link>
          <Button size="sm" variant={p.status === "review" && !open ? "outline" : "ghost"} onClick={() => setOpen(!open)}>
            {open ? "Close" : p.status === "review" ? "Correct it" : "Open"}
          </Button>
        </div>
      </div>
      {open && (
        <>
          <ul className="m-0 flex flex-col gap-1 pl-[18px] text-[#CFCBC1]">
            {p.lines.map((l, i) => (
              <li key={i} className={l.toLowerCase().includes(mk) ? "font-medium text-[#ECEAE4]" : ""}>
                {l}
              </li>
            ))}
          </ul>
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              edit.mutate({ type: "note", text: note }, { onSuccess: () => setNote("") });
            }}
          >
            <Input
              className="h-10 flex-[1_1_320px] bg-[#111111]"
              maxLength={400}
              aria-label="Correction or note"
              placeholder="Correction or note (the agent follows it)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required
            />
            <Button type="submit" variant="outline" disabled={edit.isPending}>
              Add note
            </Button>
            {p.status !== "confirmed" && p.status !== "review" && (
              <Button type="button" variant="ghost" disabled={edit.isPending} onClick={() => edit.mutate({ type: "confirm" })}>
                Mark as checked
              </Button>
            )}
          </form>
        </>
      )}
    </article>
  );
}

function People() {
  const { id } = Route.useParams();
  return (
    <ServerPage
      title="People"
      subtitle="Everyone the agent knows about. Your confirmations and notes are kept as lines the agent may not change."
      actions={
        <Link to="/servers/$id/graph" params={{ id }} className={buttonVariants({ variant: "outline", className: "no-underline" })}>
          Show as graph
        </Link>
      }
    >
      <PeopleBody />
    </ServerPage>
  );
}
